/**
 * Debug menu unlock state (sessionStorage — 앱 재시작 시 풀림).
 *
 * 진입 동선:
 *   1) 내 정보의 "앱 버전" 행을 10번 연속 탭 (3초 윈도우)
 *   2) DebugUnlockDialog 에서 토큰 입력 → BE `/api/v1/user/debug/verify-token` 검증
 *   3) 성공 시 useDebugStore.unlock() → sessionStorage 에 마킹 + 디버그 메뉴 노출
 *
 * 보안 정책:
 *   - sessionStorage 사용 → 앱 종료 시 자동 lock (디바이스 분실 시 안전)
 *   - localStorage 사용 금지 (영구 보관 위험)
 *   - 토큰 본문은 저장하지 않음. unlock 플래그만 보관.
 */
import { create } from "zustand";

const STORAGE_KEY = "pkr-debug-unlocked";
// 판매준비상태 함께 보기 토글 — 언락과 동일하게 sessionStorage(앱 재시작 시 자동 off).
const PREVIEW_READY_KEY = "pkr-debug-preview-ready";
// 스캔 화면 라벨링 모드 / 진단줄 — 같은 규칙(sessionStorage · 잠금 시 해제).
const SCAN_LABELING_KEY = "pkr-debug-scan-labeling";
const SCAN_DIAGNOSTICS_KEY = "pkr-debug-scan-diagnostics";

function readFlag(key: string): boolean {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(key) === "true";
}

function writeFlag(key: string, value: boolean): void {
  if (typeof sessionStorage === "undefined") return;
  if (value) sessionStorage.setItem(key, "true");
  else sessionStorage.removeItem(key);
}

interface DebugState {
  unlocked: boolean;
  /**
   * dev "판매준비상태 함께 보기" — true 면 전체목록/상세 조회에 preview=true 를 실어
   * READY(판매준비) 쿠지/가챠를 노출한다. 구매는 별도 게이트로 계속 차단됨.
   */
  previewReady: boolean;
  /**
   * 스캔 화면 **라벨링 모드** 노출 (2026-08-28). 인식 학습용 수집 기능이라 서비스에는
   * 없어야 한다 — 종전엔 빌드 모드(비운영)로 갈랐지만, dev 빌드로 실기기를 만지는
   * 사람에게도 늘 보여 방해가 됐다. 이제 디버그 메뉴에서 켠 세션에만 존재한다.
   */
  scanLabeling: boolean;
  /** 스캔 화면 진단줄(vote/sim/sharp · 라이브·스틸·확정 사유) 노출 */
  scanDiagnostics: boolean;
  unlock: () => void;
  lock: () => void;
  setPreviewReady: (value: boolean) => void;
  setScanLabeling: (value: boolean) => void;
  setScanDiagnostics: (value: boolean) => void;
}

export const useDebugStore = create<DebugState>((set) => ({
  unlocked: readFlag(STORAGE_KEY),
  previewReady: readFlag(PREVIEW_READY_KEY),
  scanLabeling: readFlag(SCAN_LABELING_KEY),
  scanDiagnostics: readFlag(SCAN_DIAGNOSTICS_KEY),
  unlock: () => {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, "true");
    }
    set({ unlocked: true });
  },
  lock: () => {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(STORAGE_KEY);
      // 잠금 = 디버그 상태 전체 초기화. 토글이 조용히 켜진 채 남지 않도록 함께 해제.
      sessionStorage.removeItem(PREVIEW_READY_KEY);
      sessionStorage.removeItem(SCAN_LABELING_KEY);
      sessionStorage.removeItem(SCAN_DIAGNOSTICS_KEY);
    }
    set({
      unlocked: false,
      previewReady: false,
      scanLabeling: false,
      scanDiagnostics: false,
    });
  },
  setPreviewReady: (value: boolean) => {
    writeFlag(PREVIEW_READY_KEY, value);
    set({ previewReady: value });
  },
  setScanLabeling: (value: boolean) => {
    writeFlag(SCAN_LABELING_KEY, value);
    set({ scanLabeling: value });
  },
  setScanDiagnostics: (value: boolean) => {
    writeFlag(SCAN_DIAGNOSTICS_KEY, value);
    set({ scanDiagnostics: value });
  },
}));
